import { Fragment } from 'react';
import content from '../../content/homepage';
import { comparisonData } from '../../content/comparison';
import { StatusDot } from '../../components/StatusDot';
import type { ComparisonGroup, Competitor } from '../../content/types';

function CompetitorLabel({ competitor }: { competitor: Competitor }) {
  if (!competitor.url) {
    return <>{competitor.name}</>;
  }
  return (
    <a
      href={competitor.url}
      target="_blank"
      rel="noopener noreferrer"
      className="underline decoration-[color:var(--color-foreground)]/30 underline-offset-2 transition-colors hover:text-[color:var(--color-accent-hover)] hover:decoration-[color:var(--color-accent-hover)]"
    >
      {competitor.name}
    </a>
  );
}

function Legend() {
  const { legend } = content.comparison;
  return (
    <ul
      aria-label="Comparison legend"
      className="mt-10 flex flex-wrap justify-center gap-x-10 gap-y-4 font-sans text-xs tracking-[var(--tracking-button)] text-[color:var(--color-foreground)]/80 uppercase md:text-sm"
    >
      <li className="flex items-center gap-3">
        <StatusDot level="yes" />
        <span>{legend.yes}</span>
      </li>
      <li className="flex items-center gap-3">
        <StatusDot level="partial" />
        <span>{legend.partial}</span>
      </li>
      <li className="flex items-center gap-3">
        <StatusDot level="no" />
        <span>{legend.no}</span>
      </li>
    </ul>
  );
}

function DesktopTable({
  competitors,
  groups,
}: {
  competitors: Competitor[];
  groups: ComparisonGroup[];
}) {
  return (
    <table className="hidden w-full table-fixed border-collapse font-sans text-sm md:table">
      <colgroup>
        <col className="w-[26%]" />
        {competitors.map((c) => {
          const wide = c.id === 'smith' || c.id === 'managed-agents';
          return <col key={c.id} className={wide ? 'w-[12%]' : 'w-[10%]'} />;
        })}
      </colgroup>
      <thead>
        <tr>
          <th
            scope="col"
            className="border-b border-[color:var(--color-hairline)] px-3 py-4 text-left text-base font-bold tracking-[var(--tracking-button)] text-[color:var(--color-muted)] uppercase"
          >
            Feature
          </th>
          {competitors.map((competitor) => (
            <th
              key={competitor.id}
              scope="col"
              className={
                'border-b border-[color:var(--color-hairline)] px-3 py-4 text-center text-base font-bold tracking-[var(--tracking-button)] whitespace-nowrap uppercase' +
                (competitor.emphasized
                  ? ' border-x border-x-[color:var(--color-ghost-border)] bg-[color:var(--color-foreground)]/[0.04] text-[color:var(--color-foreground)]'
                  : ' text-[color:var(--color-muted)]')
              }
            >
              <CompetitorLabel competitor={competitor} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <Fragment key={group.id}>
            <tr>
              <th
                scope="rowgroup"
                colSpan={competitors.length + 1}
                className="px-3 pt-8 pb-3 text-left font-sans text-xs tracking-[var(--tracking-eyebrow)] text-[color:var(--color-muted)] uppercase"
              >
                {group.name}
                {group.note && (
                  <span className="ml-2 text-[10px] tracking-[var(--tracking-button)] normal-case">
                    {group.note}
                  </span>
                )}
              </th>
            </tr>
            {group.rows.map((row) => (
              <tr
                key={row.featureName}
                className="border-b border-[color:var(--color-hairline)]/60"
              >
                <th
                  scope="row"
                  className="px-3 py-4 text-left font-sans text-[1.275rem] font-normal text-[color:var(--color-foreground)]"
                >
                  {row.featureName}
                </th>
                {competitors.map((competitor) => (
                  <td
                    key={competitor.id}
                    className={
                      'px-3 py-4 text-center' +
                      (competitor.emphasized
                        ? ' border-x border-x-[color:var(--color-ghost-border)] bg-[color:var(--color-foreground)]/[0.04]'
                        : '')
                    }
                  >
                    <StatusDot level={row.support[competitor.id]} />
                  </td>
                ))}
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function MobileCardList({
  competitors,
  groups,
}: {
  competitors: Competitor[];
  groups: ComparisonGroup[];
}) {
  return (
    <div className="flex flex-col gap-10 md:hidden">
      {groups.map((group) => (
        <section
          key={group.id}
          aria-labelledby={`mobile-group-${group.id}`}
          className="flex flex-col gap-4"
        >
          <h3
            id={`mobile-group-${group.id}`}
            className="font-sans text-xs tracking-[var(--tracking-eyebrow)] text-[color:var(--color-muted)] uppercase"
          >
            {group.name}
            {group.note && (
              <span className="ml-2 text-[10px] tracking-[var(--tracking-button)] normal-case">
                {group.note}
              </span>
            )}
          </h3>
          <ul className="flex flex-col gap-4">
            {group.rows.map((row) => (
              <li key={row.featureName} className="border border-[color:var(--color-hairline)] p-4">
                <h4 className="mb-3 font-sans text-sm tracking-[var(--tracking-button)] text-[color:var(--color-foreground)] uppercase">
                  {row.featureName}
                </h4>
                <ul
                  aria-label={`Support by tool for ${row.featureName}`}
                  className="grid grid-cols-4 gap-2 sm:grid-cols-7"
                >
                  {competitors.map((competitor) => (
                    <li
                      key={competitor.id}
                      className={
                        'flex flex-col items-center gap-2 py-2' +
                        (competitor.emphasized
                          ? ' border border-[color:var(--color-ghost-border)] bg-[color:var(--color-foreground)]/[0.04]'
                          : '')
                      }
                    >
                      <StatusDot level={row.support[competitor.id]} />
                      <span className="text-center font-sans text-[10px] tracking-[var(--tracking-button)] text-[color:var(--color-foreground)]/70 uppercase">
                        <CompetitorLabel competitor={competitor} />
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function ComparisonTable() {
  const { comparison } = content;
  const { competitors, groups } = comparisonData;

  return (
    <section
      id="comparison"
      aria-labelledby="comparison-heading"
      className="mx-auto max-w-[1440px] px-6 py-[var(--spacing-section-lg)] md:px-12 md:py-[var(--spacing-section-xl)]"
    >
      <h2
        id="comparison-heading"
        className="font-display mb-6 text-center text-5xl leading-tight font-medium md:text-6xl lg:text-7xl"
      >
        {comparison.sectionHeading}
      </h2>
      <p className="font-body mx-auto mb-16 max-w-3xl text-center text-base leading-relaxed text-[color:var(--color-foreground)]/75 md:mb-20 md:text-lg">
        {comparison.sectionSubheading}
      </p>

      <DesktopTable competitors={competitors} groups={groups} />
      <MobileCardList competitors={competitors} groups={groups} />

      <Legend />

      <p className="mt-10 text-center font-sans text-xs tracking-[var(--tracking-button)] text-[color:var(--color-muted)] uppercase">
        {comparison.footerNote}
      </p>
    </section>
  );
}
